import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, isValidLocale, type Locale } from '@memorai/i18n';
import { usePreferences, useUpdatePreferences } from '@memorai/api-hooks';
import type { ThemeMode, UpdateUserPreferencesInput, UserPreferences } from '@memorai/types';
import { useAuth } from '../../contexts/AuthContext';

export const UI_LOCALE_STORAGE_KEY = 'memorai-ui-locale';
export const THEME_MODE_STORAGE_KEY = 'memorai-theme-mode';
export const PRIMARY_COLOR_STORAGE_KEY = 'memorai-primary-color';
const DEFAULT_THEME_MODE: ThemeMode = 'system';
const DEFAULT_PRIMARY_COLOR = '35 131 226';

type PreferenceField = keyof Pick<UserPreferences, 'ui_locale' | 'theme_mode' | 'primary_color'>;

interface ResolvedLocaleContextValue {
    locale: Locale;
    setUiLocale: (locale: Locale) => void;
    isSaving: boolean;
    saveError: Error | null;
}

interface ResolvedPreferencesContextValue extends ResolvedLocaleContextValue {
    themeMode: ThemeMode;
    setThemeMode: (mode: ThemeMode) => void;
    primaryColor: string;
    setPrimaryColor: (color: string) => void;
    appearanceSaveError: Error | null;
}

const ResolvedPreferencesContext = createContext<ResolvedPreferencesContextValue | null>(null);

function getBrowserLocale(): Locale | undefined {
    if (typeof navigator === 'undefined') {
        return undefined;
    }

    const candidates = [
        ...(Array.isArray(navigator.languages) ? navigator.languages : []),
        navigator.language,
    ];

    for (const candidate of candidates) {
        const locale = candidate?.split('-')[0];

        if (isValidLocale(locale)) {
            return locale;
        }
    }

    return undefined;
}

function readCachedValue(key: string): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }

    try {
        return window.localStorage.getItem(key) ?? undefined;
    } catch {
        return undefined;
    }
}

function writeCachedValue(key: string, value: string) {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Storage can be unavailable in private browsing or constrained test environments.
    }
}

function readCachedLocale(): Locale | undefined {
    const cachedLocale = readCachedValue(UI_LOCALE_STORAGE_KEY);
    return isValidLocale(cachedLocale) ? cachedLocale : undefined;
}

function readCachedThemeMode(): ThemeMode | undefined {
    const cachedThemeMode = readCachedValue(THEME_MODE_STORAGE_KEY);
    return cachedThemeMode === 'light' || cachedThemeMode === 'dark' || cachedThemeMode === 'system'
        ? cachedThemeMode
        : undefined;
}

function readCachedPrimaryColor(): string | undefined {
    return readCachedValue(PRIMARY_COLOR_STORAGE_KEY);
}

function getInitialLocale(): Locale {
    return readCachedLocale() ?? getBrowserLocale() ?? DEFAULT_LOCALE;
}

export function ResolvedLocaleProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const authUserId = user?.id;
    const { data: preferences, isSuccess: hasLoadedPreferences } = usePreferences(authUserId);
    const updatePreferences = useUpdatePreferences(authUserId);
    const [locale, setLocale] = useState<Locale>(getInitialLocale);
    const [themeMode, setThemeModeState] = useState<ThemeMode>(() => readCachedThemeMode() ?? DEFAULT_THEME_MODE);
    const [primaryColor, setPrimaryColorState] = useState(() => readCachedPrimaryColor() ?? DEFAULT_PRIMARY_COLOR);
    const [localeSaveError, setLocaleSaveError] = useState<Error | null>(null);
    const [appearanceSaveError, setAppearanceSaveError] = useState<Error | null>(null);
    const localPreferenceOverrides = useRef<Partial<Record<PreferenceField, string>>>({});

    useEffect(() => {
        localPreferenceOverrides.current = {};
        setLocaleSaveError(null);
        setAppearanceSaveError(null);
    }, [authUserId]);

    useEffect(() => {
        if (!authUserId || !hasLoadedPreferences || !preferences) {
            return;
        }

        if (localPreferenceOverrides.current.ui_locale === preferences.ui_locale) {
            delete localPreferenceOverrides.current.ui_locale;
            setLocaleSaveError(null);
        } else if (!localPreferenceOverrides.current.ui_locale) {
            setLocale(preferences.ui_locale);
            writeCachedValue(UI_LOCALE_STORAGE_KEY, preferences.ui_locale);
        }

        if (localPreferenceOverrides.current.theme_mode === preferences.theme_mode) {
            delete localPreferenceOverrides.current.theme_mode;
            setAppearanceSaveError(null);
        } else if (!localPreferenceOverrides.current.theme_mode) {
            setThemeModeState(preferences.theme_mode);
            writeCachedValue(THEME_MODE_STORAGE_KEY, preferences.theme_mode);
        }

        if (localPreferenceOverrides.current.primary_color === preferences.primary_color) {
            delete localPreferenceOverrides.current.primary_color;
            setAppearanceSaveError(null);
        } else if (!localPreferenceOverrides.current.primary_color) {
            setPrimaryColorState(preferences.primary_color);
            writeCachedValue(PRIMARY_COLOR_STORAGE_KEY, preferences.primary_color);
        }
    }, [authUserId, hasLoadedPreferences, preferences]);

    const updatePreference = useCallback((updates: UpdateUserPreferencesInput) => {
        if (!authUserId) {
            return;
        }

        updatePreferences.mutate(
            updates,
            {
                onSuccess: (savedPreferences) => {
                    if (updates.ui_locale && savedPreferences.ui_locale === updates.ui_locale) {
                        delete localPreferenceOverrides.current.ui_locale;
                        setLocaleSaveError(null);
                    }

                    if (updates.theme_mode && savedPreferences.theme_mode === updates.theme_mode) {
                        delete localPreferenceOverrides.current.theme_mode;
                        setAppearanceSaveError(null);
                    }

                    if (updates.primary_color && savedPreferences.primary_color === updates.primary_color) {
                        delete localPreferenceOverrides.current.primary_color;
                        setAppearanceSaveError(null);
                    }
                },
                onError: (error) => {
                    if (updates.ui_locale) {
                        setLocaleSaveError(error);
                    }

                    if (updates.theme_mode || updates.primary_color) {
                        setAppearanceSaveError(error);
                    }
                },
            },
        );
    }, [authUserId, updatePreferences]);

    const setUiLocale = useCallback((nextLocale: Locale) => {
        setLocale(nextLocale);
        writeCachedValue(UI_LOCALE_STORAGE_KEY, nextLocale);
        localPreferenceOverrides.current.ui_locale = nextLocale;
        updatePreference({ ui_locale: nextLocale });
    }, [updatePreference]);

    const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
        setThemeModeState(nextThemeMode);
        writeCachedValue(THEME_MODE_STORAGE_KEY, nextThemeMode);
        localPreferenceOverrides.current.theme_mode = nextThemeMode;
        updatePreference({ theme_mode: nextThemeMode });
    }, [updatePreference]);

    const setPrimaryColor = useCallback((nextPrimaryColor: string) => {
        setPrimaryColorState(nextPrimaryColor);
        writeCachedValue(PRIMARY_COLOR_STORAGE_KEY, nextPrimaryColor);
        localPreferenceOverrides.current.primary_color = nextPrimaryColor;
        updatePreference({ primary_color: nextPrimaryColor });
    }, [updatePreference]);

    const value: ResolvedPreferencesContextValue = {
        locale,
        setUiLocale,
        isSaving: updatePreferences.isPending,
        saveError: localeSaveError,
        themeMode,
        setThemeMode,
        primaryColor,
        setPrimaryColor,
        appearanceSaveError,
    };

    return createElement(ResolvedPreferencesContext.Provider, { value }, children);
}

export function useResolvedLocale() {
    const context = useContext(ResolvedPreferencesContext);

    if (!context) {
        throw new Error('useResolvedLocale must be used within ResolvedLocaleProvider');
    }

    return {
        locale: context.locale,
        setUiLocale: context.setUiLocale,
        isSaving: context.isSaving,
        saveError: context.saveError,
    };
}

export function useResolvedPreferences() {
    const context = useContext(ResolvedPreferencesContext);

    if (!context) {
        throw new Error('useResolvedPreferences must be used within ResolvedLocaleProvider');
    }

    return context;
}
